import { gql, gqlFetch, invalidateGqlCacheById } from '@/service/gql'
import type { ResultOf } from 'gql.tada'
import type { SanitizedGql } from '@/util/gqlUtil'
import { getAuth } from '@/auth/authRef'
import { albumFieldsFrag } from '@/album/albumFieldsFrag'
import { artistFieldsFrag } from '@/artist/artistFieldsFrag'
import { playlistFieldsFrag } from '@/playlistdetail/playlistFieldsFrag'
import { podcastEpisodeFieldsFrag } from '@/podcast/podcastEpisodeFieldsFrag'
import { podcastShowFieldsFrag } from '@/podcast/podcastShowFieldsFrag'
import { stationFieldsFrag } from '@/service/fragments/stationFieldsFrag'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'

/**
 * Library home query — mirrors green hornet's `LibraryHomePage` query, which
 * composes four shovelers from the authenticated user's data:
 *   1. user.playlists           — playlists owned by the user
 *   2. user.followedPlaylists   — Made For You / followed playlists
 *   3. user.recentEntitiesPlayback — recently played containers
 *   4. user.recentTrackPlayback — song history
 */
const libraryHomeQuery = gql(
    `
  query libraryHome($limit: Float!, $isAuthenticated: Boolean!) {
    user {
      id
      playlists(limit: $limit, sortBy: "LAST_MODIFIED_DATE") {
        edges {
          node {
            ...playlistFields
          }
        }
      }
      followedPlaylists(limit: $limit) {
        edges {
          node {
            ...playlistFields
          }
        }
      }
      recentEntitiesPlayback(limit: $limit) {
        edges {
          node {
            __typename
            ...albumFields
            ...artistFields
            ...playlistFields
            ...podcastShowFields
            ...podcastEpisodeFields
            ...stationFields
            ...trackFields
          }
        }
      }
      recentTrackPlayback(limit: $limit) {
        edges {
          node {
            ...trackFields
          }
        }
      }
    }
  }
`,
    [
        albumFieldsFrag,
        artistFieldsFrag,
        playlistFieldsFrag,
        podcastShowFieldsFrag,
        podcastEpisodeFieldsFrag,
        stationFieldsFrag,
        trackFieldsFrag,
    ]
)

export type LibraryHomeResult = SanitizedGql<ResultOf<typeof libraryHomeQuery>>

/**
 * Stable cache id for library-home payloads. Used by mutations that
 * change the user's playlists (create / rename / delete) so the
 * library shovelers re-fetch on next view.
 *
 * Keyed by `limit` because the Library home uses 15 and
 * MyPlaylistsPage uses 50 — they're different cached entries and we
 * want both to clear on a single invalidate call.
 */
function libraryHomeCacheId(limit: number): string {
    return `libraryHome:${limit}`
}

const KNOWN_LIBRARY_HOME_LIMITS = [15, 50] as const

export async function fetchLibraryHome(limit = 15): Promise<LibraryHomeResult> {
    return gqlFetch(
        libraryHomeQuery,
        {
            limit,
            isAuthenticated: getAuth().isSignedIn(),
        },
        {
            cache: true,
            cacheId: libraryHomeCacheId(limit),
            isRetryableError: () => true,
        }
    )
}

/**
 * Drops every cached `fetchLibraryHome` payload — both the 15-item
 * Library home and the 50-item MyPlaylists list. Call after any
 * mutation that changes the user's owned playlists (create, rename,
 * delete) so the next view goes back to the gateway.
 */
export async function invalidateLibraryHomeCache(): Promise<void> {
    await Promise.all(
        KNOWN_LIBRARY_HOME_LIMITS.map((limit) => invalidateGqlCacheById(libraryHomeCacheId(limit)))
    )
}
